import React, { useEffect, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import { useTenantBundles } from '@eluve/frontend-appointment-hooks';
import { useNamedLogger } from '@eluve/logger';

interface RegenerateNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string;
  appointmentId: string;
  appointmentName: string;
  /**
   * Appointment is still active (in session). Regenerating finalizes it, so the
   * dialog requires an explicit acknowledgement before allowing the action.
   */
  isActiveAppointment: boolean;
}

/**
 * Extracts a human-readable message from an unknown ts-rest error body.
 * The endpoint's 4xx bodies all carry a `message` field.
 */
const getResponseMessage = (body: unknown, fallback: string): string => {
  if (
    typeof body === 'object' &&
    body !== null &&
    'message' in body &&
    typeof (body as { message: unknown }).message === 'string'
  ) {
    return (body as { message: string }).message;
  }
  return fallback;
};

export const RegenerateNoteDialog: React.FC<RegenerateNoteDialogProps> = ({
  open,
  onOpenChange,
  tenantId,
  appointmentId,
  appointmentName,
  isActiveAppointment,
}) => {
  const apiClient = useApiClient();
  const toast = useToast();
  const logger = useNamedLogger('RegenerateNoteDialog');
  const { bundles, loading: bundlesLoading } = useTenantBundles(tenantId);

  const [selectedTemplateBundleId, setSelectedTemplateBundleId] = useState('');
  const [processing, setProcessing] = useState(false);
  const [activeAcknowledged, setActiveAcknowledged] = useState(false);

  // Seed the selection with the first enabled bundle once they load, and keep
  // the chosen value valid if the bundle list changes underneath us.
  useEffect(() => {
    if (!open) return;
    setSelectedTemplateBundleId((current) => {
      const currentIsValid = bundles.some((bundle) => bundle.id === current);
      if (currentIsValid) return current;
      return bundles[0]?.id ?? '';
    });
  }, [bundles, open]);

  // Reset the active-appointment acknowledgement each time the dialog opens.
  useEffect(() => {
    if (open) setActiveAcknowledged(false);
  }, [open]);

  const noBundlesAvailable = !bundlesLoading && bundles.length === 0;
  const needsActiveAck = isActiveAppointment && !activeAcknowledged;

  const handleConfirm = async () => {
    if (selectedTemplateBundleId.length === 0 || needsActiveAck) return;
    setProcessing(true);
    try {
      const response = await apiClient.llm.tenantAdminRegenerateNote({
        params: { tenantId, appointmentId },
        body: { templateBundleId: selectedTemplateBundleId },
      });

      switch (response.status) {
        case 201:
          toast.success('Note regeneration started');
          onOpenChange(false);
          break;
        case 403:
          toast.error(
            getResponseMessage(
              response.body,
              'You are not permitted to regenerate notes for this tenant',
            ),
          );
          break;
        case 409:
          toast.info('This appointment already has a signed note');
          break;
        case 422:
          toast.error(
            getResponseMessage(response.body, 'Failed to regenerate note'),
          );
          break;
        default:
          toast.error('Failed to regenerate note');
          logger.error('Unexpected regenerate-note response status', {
            appointmentId,
            tenantId,
            status: response.status,
          });
      }
    } catch (error) {
      toast.error('Failed to regenerate note');
      logger.error('Failed to call tenantAdminRegenerateNote', {
        appointmentId,
        tenantId,
        error: error instanceof Error ? error : new Error(String(error)),
      });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Regenerate note</DialogTitle>
        </DialogHeader>
        <Stack gap={3}>
          <Text size="sm" color="supporting">
            Regenerate the clinical note for{' '}
            <Text inline weight="bold">
              {appointmentName}
            </Text>{' '}
            using the selected template bundle.
          </Text>
          <Stack gap={2}>
            <Label>Template bundle</Label>
            <Select
              value={selectedTemplateBundleId}
              onValueChange={setSelectedTemplateBundleId}
              disabled={processing || bundlesLoading || noBundlesAvailable}
            >
              <SelectTrigger aria-label="Template bundle">
                <SelectValue placeholder="Select a template bundle" />
              </SelectTrigger>
              <SelectContent usesPortal={false}>
                {bundles.map((bundle) => (
                  <SelectItem key={bundle.id} value={bundle.id}>
                    {bundle.displayName.length > 0
                      ? bundle.displayName
                      : 'Untitled bundle'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {noBundlesAvailable && (
              <Text size="sm" color="supporting">
                No enabled template bundles are available for this tenant.
              </Text>
            )}
          </Stack>
          {isActiveAppointment && (
            <Stack
              gap={2}
              className="rounded-lg border border-border-warning-subtle bg-bg-warning-subtle p-3"
            >
              <Stack direction="horizontal" gap={2} align="start">
                <Icon name="TriangleAlert" size="sm" className="text-warning" />
                <Text size="sm">
                  This appointment is still active. Generating a note will{' '}
                  <Text inline weight="bold">
                    complete the appointment
                  </Text>{' '}
                  and end the session.
                </Text>
              </Stack>
              <Label className="flex items-center gap-2">
                <Checkbox
                  checked={activeAcknowledged}
                  onCheckedChange={(checked) =>
                    setActiveAcknowledged(checked === true)
                  }
                />
                <Text size="sm">
                  I understand this will complete the active appointment.
                </Text>
              </Label>
            </Stack>
          )}
        </Stack>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={processing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={
              processing ||
              bundlesLoading ||
              selectedTemplateBundleId.length === 0 ||
              needsActiveAck
            }
            loading={processing}
          >
            Regenerate note
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
